import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import PWAInstallHelpModal from '../components/PWAInstallHelpModal';

const PWAInstallContext = createContext(null);
export const usePWAInstall = () => useContext(PWAInstallContext);

const DISMISS_KEY = 'find2day_pwa_dismiss';
const DISMISS_DURATION = 7 * 24 * 60 * 60 * 1000; // 7 days

// In-app browsers (WhatsApp, FB, IG, Gmail, Twitter, etc.) embed a stripped-
// down WebView that cannot install web apps. Pattern list taken from a
// composite of widely-cited UA fingerprints — we deliberately keep it short
// to avoid false positives on real Chrome/Safari.
const IN_APP_BROWSER_PATTERNS = [
  'FBAN', 'FBAV', 'FB_IAB', 'Instagram', 'Line/', 'MicroMessenger',  // Facebook, Instagram, LINE, WeChat
  'WhatsApp', 'Snapchat', 'Twitter', 'TwitterAndroid',
  'GSA/',          // Google Search App (Android in-app)
  'GoogleApp/',    // Google App
  'YJApp',         // Yahoo Japan
  'KAKAOTALK',
  'PinterestAndroid',
  'TikTok',
  // Gmail-on-Android uses Custom Tabs which DOES support install — we don't
  // flag that. Gmail-on-iOS uses Apple's SFSafariViewController which also
  // doesn't expose install but also can't be detected reliably from UA, so
  // it falls through to the iOS Safari path which is fine.
];

const detectPlatform = () => {
  const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isAndroid = /Android/.test(ua);
  const isSafari = isIOS && /Safari/.test(ua) && !/CriOS|FxiOS|OPiOS/.test(ua);
  const isStandalone =
    (typeof window !== 'undefined' &&
      (window.navigator.standalone === true ||
        window.matchMedia('(display-mode: standalone)').matches));
  const isInAppBrowser = IN_APP_BROWSER_PATTERNS.some((p) => ua.includes(p));

  return { isIOS, isAndroid, isSafari, isStandalone, isInAppBrowser, ua };
};

// Tiny PostHog wrapper — silently no-ops if PostHog isn't loaded yet (e.g.
// the snippet is still booting on a slow connection).
const captureEvent = (name, props = {}) => {
  try {
    if (typeof window !== 'undefined' && window.posthog && typeof window.posthog.capture === 'function') {
      window.posthog.capture(name, props);
    }
  } catch (_) {
    // Telemetry must never break the install UX.
  }
};

export const PWAInstallProvider = ({ children }) => {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [platform] = useState(() => detectPlatform());
  const [installed, setInstalled] = useState(() => detectPlatform().isStandalone);
  // Help-modal state — hoisted out of the banner so that the persistent
  // "Install App" menu items can open the same modal without duplicating it.
  const [helpVariant, setHelpVariant] = useState(null); // null | 'ios' | 'android' | 'desktop' | 'in-app-browser'
  // Track whether we've already emitted pwa_install_visible so we don't
  // double-count across re-renders.
  const visibleEmittedRef = useRef(false);

  const isDismissed = () => {
    try {
      const ts = localStorage.getItem(DISMISS_KEY);
      if (!ts) return false;
      return Date.now() - parseInt(ts, 10) < DISMISS_DURATION;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (platform.isStandalone) {
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    const installedHandler = () => {
      setInstalled(true);
      setShowBanner(false);
      setDeferredPrompt(null);
      captureEvent('pwa_installed', { source: 'appinstalled_event' });
    };
    window.addEventListener('appinstalled', installedHandler);

    // For mobile users with no native prompt firing, still surface the banner
    // after a short delay (3s) — covers iOS Safari + Android cases where
    // Chrome's engagement heuristics suppress the prompt.
    let timer = null;
    if ((platform.isIOS || platform.isAndroid) && !platform.isStandalone && !isDismissed()) {
      timer = setTimeout(() => {
        if (!isDismissed()) setShowBanner(true);
      }, 3000);
    }

    return () => {
      if (timer) clearTimeout(timer);
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, [platform]);

  // Emit pwa_install_visible exactly once per session (banner OR menu render).
  const markInstallSurfaceVisible = useCallback((surface) => {
    if (visibleEmittedRef.current) return;
    visibleEmittedRef.current = true;
    captureEvent('pwa_install_visible', { surface, ...platform });
  }, [platform]);

  // Resolve the right modal variant for the current device/browser.
  const resolveHelpVariant = useCallback(() => {
    if (platform.isInAppBrowser) return 'in-app-browser';
    if (platform.isIOS) return 'ios';
    if (platform.isAndroid) return 'android';
    return 'desktop';
  }, [platform]);

  // Single entry point used by both the banner and the persistent menu items.
  // - Native prompt available → call it, capture outcome.
  // - Otherwise → open the platform-appropriate help modal.
  const openInstallFlow = useCallback(async (surface = 'unknown') => {
    captureEvent('pwa_install_clicked', { surface, hasNativePrompt: !!deferredPrompt });

    if (deferredPrompt) {
      try {
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        captureEvent('pwa_install_prompt_outcome', { surface, outcome });
        setDeferredPrompt(null);
        if (outcome === 'accepted') {
          setShowBanner(false);
          // `appinstalled` will fire shortly and flip `installed` + emit
          // `pwa_installed`. We don't double-emit here.
          return true;
        }
        return false;
      } catch (err) {
        captureEvent('pwa_install_prompt_outcome', { surface, outcome: 'error', error: String(err) });
        return false;
      }
    }

    // No native prompt → open the help modal.
    const v = resolveHelpVariant();
    setHelpVariant(v);
    captureEvent('pwa_install_help_opened', { surface, variant: v });
    return false;
  }, [deferredPrompt, resolveHelpVariant]);

  const dismissBanner = useCallback(() => {
    try { localStorage.setItem(DISMISS_KEY, Date.now().toString()); } catch (_) { /* localStorage unavailable */ }
    setShowBanner(false);
  }, []);

  const closeHelpModal = useCallback(() => setHelpVariant(null), []);

  return (
    <PWAInstallContext.Provider value={{
      showBanner,
      platform,
      installed,
      canNativeInstall: !!deferredPrompt,
      // Back-compat for the existing PWAInstallBanner (which still calls these
      // directly). New code should prefer openInstallFlow().
      triggerInstall: () => openInstallFlow('banner'),
      dismissBanner,
      // New API for the persistent menu items.
      openInstallFlow,
      markInstallSurfaceVisible,
      resolveHelpVariant,
    }}>
      {children}
      {/* Help modal is rendered once at the context level so banner + menu
          items + dashboard sidebar all open the same instance. */}
      {helpVariant && (
        <PWAInstallHelpModal
          variant={helpVariant}
          t={t}
          onClose={closeHelpModal}
        />
      )}
    </PWAInstallContext.Provider>
  );
};
